const VIDEO_DEBUG = process.env.REACT_APP_VIDEO_DEBUG === 'true';
const vlog = VIDEO_DEBUG ? console.log.bind(console) : () => {};

class VideoService {
  constructor(backendService, options = {}) {
    this.backendService = backendService || null;
    this.loadBroadcastSdk = options.loadBroadcastSdk || (() => import('amazon-ivs-web-broadcast'));
    this.mediaStream = null;
    this.broadcastClient = null;
    this.currentActivityId = null;
    this.currentSessionId = null;
    this.isBroadcasting = false;
    this._onStreamChangeSubscribers = [];
    this._onErrorSubscribers = [];
    this._onBroadcastStateSubscribers = [];
  }

  async startWorkoutBroadcast(activityId) {
    if (!this.backendService) throw new Error('Workout streaming service is unavailable.');
    if (!activityId) throw new Error('Workout ID is required to start streaming.');
    if (this.isBroadcasting && this.currentActivityId === String(activityId)) {
      return { started: true, session_id: this.currentSessionId };
    }
    if (this.isBroadcasting && this.currentActivityId !== String(activityId)) {
      await this.stopWorkoutBroadcast(this.currentActivityId);
    }

    let settings;
    try {
      settings = await this.backendService.getWorkoutStreamingSettings();
    } catch (error) {
      // Unknown opt-in is not permission to capture. No camera start was
      // attempted, so do not report this optional preference read as one.
      return { started: false, skipped: true, reason: 'settings_unavailable' };
    }
    if (settings?.enabled !== true) return { started: false, disabled: true };

    let provisioned = null;
    try {
      const constraints = {
        video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 1280 } },
        audio: settings.audio_enabled === true,
      };
      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      this._notifyStreamChange(this.mediaStream);

      provisioned = await this.backendService.startWorkoutLiveStream(
        activityId,
        settings.default_audiences || []
      );
      const sdk = await this.loadBroadcastSdk();
      const IVSBroadcastClient = sdk.default;
      if (!IVSBroadcastClient?.create || !sdk.STANDARD_PORTRAIT) {
        throw new Error('This browser cannot initialize the IVS broadcast client.');
      }
      this.broadcastClient = IVSBroadcastClient.create({
        streamConfig: sdk.STANDARD_PORTRAIT,
        ingestEndpoint: provisioned.ingest_endpoint,
      });
      await this.broadcastClient.addVideoInputDevice(this.mediaStream, 'workout-camera', { index: 0 });
      if (settings.audio_enabled) {
        await this.broadcastClient.addAudioInputDevice(this.mediaStream, 'workout-audio');
      }
      await this.broadcastClient.startBroadcast(provisioned.stream_key);

      this.currentActivityId = String(activityId);
      this.currentSessionId = provisioned.session_id;
      this.isBroadcasting = true;
      this._notifyBroadcastState({ status: 'live', activity_id: this.currentActivityId });
      vlog('[VideoService] Workout broadcast started', this.currentActivityId);
      return { started: true, session_id: this.currentSessionId };
    } catch (error) {
      if (provisioned?.session_id) {
        await this.backendService.stopWorkoutLiveStream(activityId, provisioned.session_id).catch(() => {});
      }
      this._cleanupLocalBroadcast();
      this._notifyError(error);
      throw error;
    }
  }

  async stopWorkoutBroadcast(activityId = this.currentActivityId, { notifyBackend = true } = {}) {
    const sessionId = this.currentSessionId;
    const activeActivityId = activityId || this.currentActivityId;
    try {
      if (this.broadcastClient) this.broadcastClient.stopBroadcast();
      if (notifyBackend && this.backendService && activeActivityId && sessionId) {
        await this.backendService.stopWorkoutLiveStream(activeActivityId, sessionId);
      }
    } catch (error) {
      // The backend also revokes publishing when the workout stops/completes.
      console.warn('[VideoService] Could not confirm stream stop:', error.message);
    } finally {
      this._cleanupLocalBroadcast();
      this._notifyBroadcastState({ status: 'ended', activity_id: String(activeActivityId || '') });
    }
  }

  _cleanupLocalBroadcast() {
    if (this.broadcastClient) {
      try { this.broadcastClient.delete(); } catch (error) { vlog('[VideoService] Client cleanup failed', error); }
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
    }
    this.broadcastClient = null;
    this.mediaStream = null;
    this.currentActivityId = null;
    this.currentSessionId = null;
    this.isBroadcasting = false;
    this._notifyStreamChange(null);
  }

  async stopCamera() {
    await this.stopWorkoutBroadcast();
  }

  getStream() {
    return this.mediaStream;
  }

  isCameraOn() {
    return Boolean(this.mediaStream?.getVideoTracks?.().some(track => track.readyState === 'live'));
  }

  isBroadcastingWorkout(activityId) {
    return this.isBroadcasting && String(activityId) === this.currentActivityId;
  }

  onStreamChange(callback) {
    if (typeof callback === 'function') {
      this._onStreamChangeSubscribers.push(callback);
      callback(this.mediaStream);
    }
    return () => {
      this._onStreamChangeSubscribers = this._onStreamChangeSubscribers.filter(cb => cb !== callback);
    };
  }

  onError(callback) {
    if (typeof callback === 'function') this._onErrorSubscribers.push(callback);
    return () => {
      this._onErrorSubscribers = this._onErrorSubscribers.filter(cb => cb !== callback);
    };
  }

  onBroadcastState(callback) {
    if (typeof callback === 'function') this._onBroadcastStateSubscribers.push(callback);
    return () => {
      this._onBroadcastStateSubscribers = this._onBroadcastStateSubscribers.filter(cb => cb !== callback);
    };
  }

  _notifyStreamChange(stream) {
    this._onStreamChangeSubscribers.forEach(callback => callback(stream));
  }

  _notifyError(error) {
    this._onErrorSubscribers.forEach(callback => callback(error));
  }

  _notifyBroadcastState(state) {
    this._onBroadcastStateSubscribers.forEach(callback => callback(state));
  }

  static async hasCamera() {
    if (!navigator.mediaDevices?.enumerateDevices) return false;
    try {
      return (await navigator.mediaDevices.enumerateDevices()).some(device => device.kind === 'videoinput');
    } catch (error) {
      return false;
    }
  }
}

export default VideoService;
